import { observer } from "mobx-react-lite";
import { Box, BoxProps, Center, Flex, Image, Text } from "@chakra-ui/react";

import { APIOption, Dictionary } from "@noli/core";
import { langStore, translate } from "@noli/business";

import { themeStore } from "@/stores/theme";
import SchemaProperty from "@/components/Property/SchemaProperty";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import TextTrigger, { TextTriggerProps } from "./TextTrigger";
import { genBlock } from "@/utils/bem";

type SingleButtonProps = {
  src: any;
  caption: string;
  storeValue: string;
  setStoreValue: (value: any) => void;
};
const SingleButton = observer<SingleButtonProps>(({ src, caption, storeValue, setStoreValue }) => {
  const lang = langStore.tgt;
  const { captionColor } = themeStore.styles;

  const size = 48;
  const captionSize = 26;
  // UI will be strange for other lang because the caption is usually too long
  const captionText = lang === "zh" ? translate(caption, lang) : "";

  return (
    <Box className="c-single-button" w={`${size}px`} h={`${size + captionSize}px`}>
      <SchemaProperty
        identifier={caption}
        lang={lang}
        noTitle
        apiKey=""
        apiOption={new APIOption("bool", storeValue === caption)}
        value={storeValue === caption}
        setValue={() => () => setStoreValue(caption)}
        booleanIcon={<Image src={src} w={`${size - 4}px`} h={`${size - 4}px`} />}
        w={`${size}px`}
        h={`${size}px`}
        borderRadius="4px"
        booleanCheckedPropsFn={(checked) => ({
          border: "2px",
          borderColor: checked ? "brand.primary" : "transparent",
        })}
      />
      <Center>
        <Text pt="2px" color={captionColor} fontSize="12px" fontWeight={300}>
          {captionText}
        </Text>
      </Center>
    </Box>
  );
});
type ButtonGalleryProps = {
  panelTitle: string;
  collections: readonly string[];
  images: Dictionary<any>;
  storeValue: string;
  setStoreValue: (value: any) => void;
};

const block = genBlock("c-button-gallery");
function ButtonGallery({
  panelTitle,
  collections,
  images,
  storeValue,
  setStoreValue,
  ...others
}: ButtonGalleryProps & Omit<TextTriggerProps, "popupGenerator">) {
  const { panelBg } = themeStore.styles;

  const panelW = 200;
  const panelH = 300;

  return (
    <TextTrigger
      panelTitle={panelTitle}
      storeValue={storeValue}
      setStoreValue={setStoreValue}
      popupGenerator={(value, setValue) => (
        <SubPanelWithScrollBar
          w={`${panelW}px`}
          h={`${panelH}px`}
          bg={panelBg}
          // px="16px"
          className={block("popup")}
          sx={{
            [`& > .c-subpanel-with-scroll-bar-inner > .${block("collections")}`]: {
              w: `${panelW - 32}px`,
            },
            "& > .c-subpanel-with-scroll-bar-inner": {
              px: "16px",
            },
          }}
        >
          <Flex
            w={`${panelW - 32}px`}
            className={block("collections")}
            // mx="16px"
            mt="16px"
            mb="10px"
            wrap="wrap"
            align="center"
            justifyContent="space-between"
          >
            {collections.map((caption) => (
              <SingleButton
                key={caption}
                src={images[caption]}
                caption={caption}
                storeValue={value}
                setStoreValue={setValue}
              />
            ))}
          </Flex>
        </SubPanelWithScrollBar>
      )}
      safeDistance={panelH - (others.textH ?? 60)}
      smartAlign={{
        offset: [24, 10],
        points: ["bl", "br"],
      }}
      {...others}
    />
  );
}

export default observer(ButtonGallery);
